<template>
  <div>
    <!-- MODO NORMAL: exibe seletor de refil + datas + botão -->
    <div v-if="!printMode">
      <h6>Análise de Despesas por Refil</h6>
      <p></p>
      <div class="row mb-3">
        <!-- Seletor de Refil -->
        <div class="col-auto">
          <label>Refil:</label>
          <select v-model="subcontaIdLocal" class="form-control">
            <option value="" disabled>Selecione um refil</option>
            <option
              v-for="opt in subcontasOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>

        <!-- Data Início -->
        <div class="col-auto">
          <label>Data Início:</label>
          <input type="date" v-model="dataInicioLocal" class="form-control" />
        </div>

        <!-- Data Fim -->
        <div class="col-auto">
          <label>Data Fim:</label>
          <input type="date" v-model="dataFimLocal" class="form-control" />
        </div>

        <!-- Botão Executar -->
        <div class="col-auto align-self-end">
          <button @click="abrirPrint" class="btn btn-primary">
            Executar
          </button>
        </div>
      </div>
    </div>

    <!-- MODO IMPRESSÃO: busca dados no backend e exibe tabela -->
    <div v-else>
      <!-- <h4>{{ printTitle }}</h4> -->

      <div v-if="loading">Carregando…</div>

      <div v-else-if="resultados.length">
      <p class="text-center" style="font-size: 16px">
        {{ subtitle }}
      </p>
        <table class="table table-striped">
          <thead>
            <tr>
              <th>DESCRIÇÃO</th>
              <th class="valor-cell">VALOR</th>
              <th class="text-center">FORMA DE PAGAMENTO</th>
              <th class="text-center">DATA VENCIMENTO</th>
              <th class="text-center">DATA PAGAMENTO</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="item in resultados" :key="item.registro_id">
              <td>{{ item.descricao }}</td>
              <td class="valor-cell">{{ formatarMoeda(item.valor) }}</td>
              <td class="text-center">{{ item.forma_pagamento }}</td>
              <td class="text-center">{{ globalUtils.formatarData(item.data_vencimento) }}</td>
              <td class="text-center">{{ globalUtils.formatarData(item.data_pagamento) }}</td>
            </tr>
          </tbody>
          <!-- tfoot: exibe somente se total > 0 -->
          <tfoot v-if="total > 0">
            <tr>
              <td colspan="2"><strong>Total</strong></td>
              <td class="valor-cell text-right"><strong>{{ formatarMoeda(total) }}</strong></td>
              <td colspan="3"></td>
            </tr>
          </tfoot>
        </table>
      </div>

      <div v-else>
        <p>Nenhum registro encontrado para este refil no período.</p>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from '@/services/axios'
import globalUtils from '@/utils/globalUtils.js'


// Props vindas da rota de impressão
const props = defineProps({
  printMode:  { type: Boolean, default: false },
  dataInicio: String,
  dataFim:    String,
  subcontaId: [String, Number],
  printTitle: { type: String, default: 'Análise de Despesas por Refil' },
  subtitle:   { type: String, default: '' }
})

const router = useRouter()

// --- ESTADOS PARA MODO NORMAL ---
const subcontasOptions = ref([])
const subcontaIdLocal   = ref('')
const dataInicioLocal   = ref('')
const dataFimLocal      = ref('')

// --- ESTADOS PARA MODO IMPRESSÃO ---
const loading    = ref(false)
const resultados = ref([])
const total      = ref(0)

// 1 único onMounted que trata tanto “modo normal” quanto “modo impressão”
onMounted(async () => {
  // Se não for modo impressão, carrega apenas o dropdown de subcontas
  if (!props.printMode) {
    try {
      const res = await axios.get('/gestao/relatorios/despesas/subcontas-entradas')
      const listaBruta = Array.isArray(res.data)
        ? res.data
        : Array.isArray(res.data.data)
          ? res.data.data
          : []

      const opcoes = listaBruta.map(sc => ({
        label: sc.nome,
        value: sc.id
      }))

      // Insere "Entradas da Loja" no início
      // opcoes.unshift({ label: 'Entradas da Loja', value: 'entradas' })
      subcontasOptions.value = opcoes
    } catch (err) {
      console.error('Erro ao carregar subcontas:', err)
      subcontasOptions.value = [
        { label: 'Entradas da Loja', value: 'entradas' }
      ]
    }
    
    // Sai antes de ir para o bloco de “printMode”
    return
  }

  // Validações básicas
  if (!props.dataInicio || !props.dataFim || !props.subcontaId) {
    console.warn('[AnalisePorRefil] Saindo: faltam props dataInicio/dataFim/subcontaId')
    return
  }

  loading.value = true
  try {
    const res = await axios.get('/gestao/relatorios/despesas/analise-por-refil', {
      params: {
        subcontaId: props.subcontaId,
        dataInicio: props.dataInicio,
        dataFim:    props.dataFim
      }
    })
    // Agora o back-end devolve { items: [...], total: 1234.56 }
    resultados.value = Array.isArray(res.data.items) ? res.data.items : []
    total.value      = parseFloat(res.data.total) || 0
  } catch (err) {
    console.error('Erro ao buscar análise por refil (print):', err)
    resultados.value = []
    total.value      = 0
  } finally {
    loading.value = false
  }
})

// 3) Quando o usuário clicar em “Executar” no modo normal, redireciona para a rota de impressão
function abrirPrint() {
  if (!subcontaIdLocal.value || !dataInicioLocal.value || !dataFimLocal.value) return

  // 1) Encontrar o objeto de opção cujo valor é subcontaIdLocal
  const selected = subcontasOptions.value.find(
    (opt) => String(opt.value) === String(subcontaIdLocal.value)
  )
  const labelSubconta = selected ? selected.label : '—'

  // 2) Formatar as datas para “DD/MM/YYYY”
  const inicioFormatado = globalUtils.formatarData(dataInicioLocal.value)
  const fimFormatado    = globalUtils.formatarData(dataFimLocal.value)

  // 3) Montar o subtitle completo, incluindo período
  const subtitle = `${labelSubconta} - período de ${inicioFormatado} a ${fimFormatado}`

  router.push({
    path: `/gestao/relatorios/print/despesas-analise-por-refil`,
    query: {
      title:      'Análise de Despesas por Refil',
      subcontaId: subcontaIdLocal.value,
      dataInicio: dataInicioLocal.value,
      dataFim:    dataFimLocal.value,
      subtitle:   subtitle
    }
  })
}

// --- FUNÇÕES AUXILIARES DE FORMATAÇÃO ---

function formatarMoeda(num) {
  if (num == null) return ''
  return Number(num).toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  })
}
</script>

<style scoped>
.mb-3 {
  margin-bottom: 1rem;
}
.valor-cell {
  text-align: right !important;
}

</style>
